import { useNavigate } from "@tanstack/react-router"
import React, { useState } from "react"

import { trpc } from "../../lib/trpc"

interface MetricCard {
  title: string
  value: string | number
  change?: string
  changeType?: "positive" | "negative" | "neutral"
  icon: string
}

const MetricCard: React.FC<MetricCard> = ({ title, value, change, changeType, icon }) => {
  const changeColor = changeType === "positive" ? "text-green-600" : changeType === "negative" ? "text-destructive" : ""

  return (
    <div className="bg-background  rounded-lg p-6 shadow-sm border border-border dark:border-gray-700">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium  dark:text-gray-400">{title}</p>
          <p className="text-2xl font-semibold  dark:text-white">{value}</p>
          {change && (
            <p className={`text-sm ${changeColor}`}>
              {changeType === "positive" ? "↗" : changeType === "negative" ? "↘" : "→"} {change}
            </p>
          )}
        </div>
        <div className="text-3xl">{icon}</div>
      </div>
    </div>
  )
}

const Analytics: React.FC = () => {
  const navigate = useNavigate()
  const [selectedCampaign, setSelectedCampaign] = useState<string>("")
  const [dateRange, setDateRange] = useState<string>("7d")

  // Query campaigns for selection
  const { data: campaigns } = trpc.crm.campaigns.list.useQuery({
    limit: 100,
    status: "active",
  })

  // Query analytics for selected campaign
  const { data: analytics, isLoading } = trpc.crm.campaigns.getAnalytics.useQuery(
    {
      campaignId: selectedCampaign,
      dateRange: {
        start: new Date(Date.now() - (dateRange === "7d" ? 7 : dateRange === "30d" ? 30 : 90) * 24 * 60 * 60 * 1000),
        end: new Date(),
      },
    },
    {
      enabled: !!selectedCampaign,
    },
  )

  // Query all conversations for overall stats
  const { data: allConversations } = trpc.crm.conversations.list.useQuery({
    limit: 1000,
  })

  // Calculate overall metrics
  const overallMetrics: MetricCard[] = [
    {
      title: "Total Conversations",
      value: allConversations?.total || 0,
      icon: "💬",
      change: "+12% this week",
      changeType: "positive",
    },
    {
      title: "Response Rate",
      value: "74%",
      icon: "📈",
      change: "+3% vs last week",
      changeType: "positive",
    },
    {
      title: "Human Takeovers",
      value: allConversations?.conversations?.filter((c) => c.humanTakeover).length || 0,
      icon: "👨‍💻",
      change: "-8% vs last week",
      changeType: "positive",
    },
    {
      title: "Avg Response Time",
      value: "2.3h",
      icon: "⏱️",
      change: "-15min vs last week",
      changeType: "positive",
    },
  ]

  const campaignMetrics: MetricCard[] = analytics
    ? [
        {
          title: "Active Conversations",
          value: analytics.activeConversations,
          icon: "🔥",
        },
        {
          title: "Total Messages",
          value: analytics.totalMessages,
          icon: "📨",
        },
        {
          title: "Response Rate",
          value: `${Math.round(analytics.responseRate * 100)}%`,
          icon: "📊",
        },
        {
          title: "Avg Messages/Conv",
          value: Math.round(analytics.averageMessagesPerConversation),
          icon: "💭",
        },
      ]
    : []

  return (
    <div className="h-full overflow-auto bg-foregroundx ">
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold  dark:text-white">Analytics</h1>
              <p className="mt-2  dark:text-gray-400">Track performance and optimize your campaigns</p>
            </div>
            <div className="flex gap-2">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background   dark:text-white"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
              </select>
            </div>
          </div>
        </div>

        {/* Overall Metrics */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold  dark:text-white mb-4">Overall Performance</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {overallMetrics.map((metric, index) => (
              <MetricCard key={index} {...metric} />
            ))}
          </div>
        </div>

        {/* Campaign Selection */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold  dark:text-white mb-4">Campaign Analytics</h2>
          <div className="mb-4">
            <select
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background   dark:text-white"
            >
              <option value="">Select a campaign</option>
              {campaigns?.campaigns?.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </select>
          </div>

          {selectedCampaign && analytics && !isLoading && (
            <div>
              {/* Campaign Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {campaignMetrics.map((metric, index) => (
                  <MetricCard key={index} {...metric} />
                ))}
              </div>

              {/* Stage Distribution */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-background  rounded-lg p-6 shadow-sm border border-border dark:border-gray-700">
                  <h3 className="text-lg font-semibold  dark:text-white mb-4">Stage Distribution</h3>
                  {Object.entries(analytics.stageDistribution).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(analytics.stageDistribution).map(([stage, count]) => (
                        <div key={stage} className="flex items-center justify-between">
                          <span className="text-primary dark:text-gray-300">{stage}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-32 bg-foregroundx dark:bg-gray-700 rounded-full h-2">
                              <div
                                className="bg-blue-600 h-2 rounded-full"
                                style={{
                                  width: `${Math.min(
                                    100,
                                    (count / Math.max(...Object.values(analytics.stageDistribution))) * 100,
                                  )}%`,
                                }}
                              />
                            </div>
                            <span className="text-sm font-medium  dark:text-white w-8 text-right">{count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No stage data available</p>
                  )}
                </div>

                <div className="bg-background  rounded-lg p-6 shadow-sm border border-border dark:border-gray-700">
                  <h3 className="text-lg font-semibold  dark:text-white mb-4">Conversation Status</h3>
                  {Object.entries(analytics.conversationStatuses).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(analytics.conversationStatuses).map(([status, count]) => (
                        <div key={status} className="flex items-center justify-between">
                          <span className="text-primary dark:text-gray-300">
                            {status.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                          </span>
                          <div className="flex items-center gap-2">
                            <div className="w-32 bg-foregroundx dark:bg-gray-700 rounded-full h-2">
                              <div
                                className="bg-green-600 h-2 rounded-full"
                                style={{
                                  width: `${Math.min(
                                    100,
                                    (count / Math.max(...Object.values(analytics.conversationStatuses))) * 100,
                                  )}%`,
                                }}
                              />
                            </div>
                            <span className="text-sm font-medium  dark:text-white w-8 text-right">{count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No status data available</p>
                  )}
                </div>
              </div>

              {/* Last Activity */}
              {analytics.lastActivity && (
                <div className="mt-8 bg-background  rounded-lg p-6 shadow-sm border border-border dark:border-gray-700">
                  <h3 className="text-lg font-semibold  dark:text-white mb-2">Campaign Activity</h3>
                  <p className=" dark:text-gray-400">
                    Last activity: {new Date(analytics.lastActivity).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          )}

          {selectedCampaign && isLoading && (
            <div className="text-center py-8">
              <div className="text-gray-500">Loading campaign analytics...</div>
            </div>
          )}

          {!selectedCampaign && (
            <div className="text-center py-8">
              <div className="text-gray-500 mb-4">Select a campaign to view detailed analytics</div>
              {campaigns?.campaigns && campaigns.campaigns.length === 0 && (
                <button
                  onClick={() => navigate({ to: "/crm/campaigns" })}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Create Your First Campaign
                </button>
              )}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-background  rounded-lg p-6 shadow-sm border border-border dark:border-gray-700">
          <h3 className="text-lg font-semibold  dark:text-white mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate({ to: "/crm/inbox" })}
              className="p-4 border border-gray-300 dark:border-gray-600 rounded-lg  dark:hover:bg-gray-700 transition-colors"
            >
              <div className="text-2xl mb-2">📬</div>
              <div className="font-medium  dark:text-white">Review Inbox</div>
              <div className="text-sm text-gray-500">Check conversations needing attention</div>
            </button>

            <button
              onClick={() => navigate({ to: "/crm/campaigns" })}
              className="p-4 border border-gray-300 dark:border-gray-600 rounded-lg  dark:hover:bg-gray-700 transition-colors"
            >
              <div className="text-2xl mb-2">🚀</div>
              <div className="font-medium  dark:text-white">Optimize Campaigns</div>
              <div className="text-sm text-gray-500">Adjust settings and templates</div>
            </button>

            <button
              onClick={() => navigate({ to: "/crm/templates" })}
              className="p-4 border border-gray-300 dark:border-gray-600 rounded-lg  dark:hover:bg-gray-700 transition-colors"
            >
              <div className="text-2xl mb-2">📝</div>
              <div className="font-medium  dark:text-white">Update Templates</div>
              <div className="text-sm text-gray-500">Improve message performance</div>
            </button>
          </div>
        </div>

        {/* Future Features Placeholder */}
        <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-300 mb-2">🔮 Coming Soon</h3>
          <div className="text-blue-700 dark:text-blue-400 space-y-1">
            <div>• Conversion funnel visualization</div>
            <div>• Template A/B testing results</div>
            <div>• Real-time performance charts</div>
            <div>• Export analytics reports</div>
            <div>• Automated insights and recommendations</div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Analytics
